import { BubbleMenu } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { Wand2Icon, Loader2, EyeOffIcon, EyeIcon } from "lucide-react";
import axios from "axios";
import { toast } from "react-hot-toast";
import EnhanceCompareModal from "../EnhanceCompareModal";
import cn from "classnames";
import { convertHtmlToPlainText } from "../../utilities/converHtmlToPlainText";
import { replaceTextPluginKey } from "./ReplaceText";
import { getRedactionsInRange } from "./RedactionsExtension";
import { undoDepth, redoDepth } from "@tiptap/pm/history";
import type { RedactionType } from "../../db/schema";

const FloatingMenu = ({
  editor,
  onChange,
  caseId,
  caseRedactions,
  setCaseRedactions,
  onEnableRedactionHighlight,
  onOpenRedactionsTab,
  onAiGenerated,
  onAiReverted,
}: {
  editor: any;
  onChange: any;
  caseId?: string;
  caseRedactions?: RedactionType[];
  setCaseRedactions?: React.Dispatch<React.SetStateAction<RedactionType[]>>;
  onEnableRedactionHighlight?: () => void;
  onOpenRedactionsTab?: () => void;
  onAiGenerated?: () => void;
  onAiReverted?: () => void;
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [originalContent, setOriginalContent] = useState("");
  const [enhancedContent, setEnhancedContent] = useState("");
  const [selectionRange, setSelectionRange] = useState({ from: 0, to: 0 });
  const auditIdRef = useRef<string | null>(null);
  const enhancedTextRef = useRef<string>("");
  // Redactions whose occurrences overlap the current selection. When non-empty
  // the bubble menu shows "Unredact" instead of "Redact". Recomputed on every
  // selection change so the label stays accurate as the user drags.
  const [redactionsInSelection, setRedactionsInSelection] = useState<
    RedactionType[]
  >([]);
  // Track undo depth right after enhance — if it drops below this, enhance was undone
  const enhanceUndoDepthRef = useRef<number | null>(null);
  // "tracking" = watching for undo, "undone" = watching for redo, null = inactive
  const trackingStateRef = useRef<"tracking" | "undone" | null>(null);

  // Listen for editor transactions to detect undo/redo of enhancement via history depth
  useEffect(() => {
    if (!editor) return;

    const handleTransaction = () => {
      if (!trackingStateRef.current || enhanceUndoDepthRef.current === null)
        return;

      const currentUndoDepth = undoDepth(editor.state);

      if (trackingStateRef.current === "tracking") {
        // Watching for undo: if undo depth drops below enhance depth, enhance was undone
        if (currentUndoDepth < enhanceUndoDepthRef.current && auditIdRef.current) {
          axios
            .delete(`/api/audits/${auditIdRef.current}`, {
              withCredentials: true,
            })
            .catch((err) =>
              console.error("Failed to delete reverted audit:", err)
            );
          auditIdRef.current = null;
          trackingStateRef.current = "undone";
          onAiReverted?.();
        }
      } else if (trackingStateRef.current === "undone") {
        if (
          currentUndoDepth >= enhanceUndoDepthRef.current &&
          !auditIdRef.current
        ) {
          // Redo detected — undo depth restored to enhance level
          if (caseId) {
            axios
              .post("/api/audits", {
                caseId,
                content: enhancedTextRef.current,
                audit_type: "enhanced_writing",
              })
              .then((res) => {
                auditIdRef.current = res.data.audit.id;
              })
              .catch((err) =>
                console.error("Failed to re-create audit on redo:", err)
              );
            onAiGenerated?.();
          }
          trackingStateRef.current = "tracking";
        } else if (redoDepth(editor.state) === 0) {
          // New edit was made — redo stack cleared, enhance can't be redone
          trackingStateRef.current = null;
          enhanceUndoDepthRef.current = null;
        }
      }
    };

    editor.on("transaction", handleTransaction);
    return () => editor.off("transaction", handleTransaction);
  }, [editor, caseId, onAiGenerated, onAiReverted]);

  // Recompute which redactions overlap the selection whenever the selection or
  // the redaction list changes, so the button label flips between Redact and
  // Unredact. Detection works even when highlighting is toggled off.
  useEffect(() => {
    if (!editor) return;
    const recompute = () => {
      const { from, to } = editor.state.selection;
      if (from === to) {
        setRedactionsInSelection([]);
        return;
      }
      setRedactionsInSelection(
        getRedactionsInRange(editor.state.doc, caseRedactions || [], from, to),
      );
    };
    recompute();
    editor.on("selectionUpdate", recompute);
    editor.on("transaction", recompute);
    return () => {
      editor.off("selectionUpdate", recompute);
      editor.off("transaction", recompute);
    };
  }, [editor, caseRedactions]);

  const handleEnhance = async () => {
    if (!editor || isEnhancing) return;

    const { from, to } = editor.state.selection;
    const selectedContent = editor.state.doc
      .slice(from, to)
      .content.textBetween(0, to - from, "\n", "\n");

    if (!selectedContent) {
      toast.error("Please select some text to enhance");
      return;
    }

    const words = selectedContent
      .trim()
      .split(/\s+/)
      .filter((word: string) => word.length > 0);
    const isShortSelection = words.length < 7;

    // Calculate selection percentage to determine if we should use preferences
    const fullReportText = convertHtmlToPlainText(editor.getHTML());
    const totalLength = fullReportText.length;
    const selectedLength = selectedContent.length;
    const selectionPercentage = totalLength > 0 ? (selectedLength / totalLength) * 100 : 0;
    const isLargeSelection = selectionPercentage >= 75; // 75% threshold for preference-based enhancement
    const isFullReport = selectionPercentage >= 95; // 95% threshold to include header/footer

    // Check if selection includes an h1 (title) element
    let includesTitle = false;
    const selectedDoc = editor.state.doc.slice(from, to);
    selectedDoc.content.forEach((node: any) => {
      if (node.type.name === 'heading' && node.attrs.level === 1) {
        includesTitle = true;
      }
    });

    try {
      setIsEnhancing(true);
      setSelectionRange({ from, to });
      setOriginalContent(selectedContent);

      // Only send full report context for longer selections (7+ words)
      const fullReport = isShortSelection
        ? null
        : fullReportText;

      // Choose endpoint based on selection size
      const endpoint = isLargeSelection ? "/api/enhance/with-preferences" : "/api/enhance/reword";

      const response = await axios.post(
        endpoint,
        {
          content: selectedContent,
          fullReport,
          isShortSelection, // Let the Lambda know this is a short selection
          caseId, // Pass caseId for preference-based enhancement
          isFullReport, // Flag to include header/footer when nearly entire report is selected
          includesTitle, // Flag to indicate if h1 title is in selection
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Clean up the enhanced content to preserve line breaks and remove empty p tags
      let cleanedContent = response.data.enhancedContent;

      // Convert newlines within paragraph tags to <br> tags
      // This preserves line breaks when switching between cases
      cleanedContent = cleanedContent.replace(/<p>([\s\S]*?)<\/p>/g, (_match: string, content: string) => {
        // Replace newlines within the paragraph with <br> tags
        const withBreaks = content.replace(/\n/g, '<br>');
        return `<p>${withBreaks}</p>`;
      });

      // Now remove whitespace between tags and empty p tags
      cleanedContent = cleanedContent
        .replace(/>\s+</g, '><')  // Remove whitespace between tags
        .replace(/<p>\s*<\/p>/g, '')  // Remove empty <p> tags with whitespace
        .replace(/<p><\/p>/g, '')  // Remove completely empty <p> tags
        .trim();

      // Apply styling to placeholder text {{...}} for visual consistency
      // Use the same styles as ReplaceText but without cursor pointer
      // Hide the brackets using replace-text-bracket class
      cleanedContent = cleanedContent.replace(
        /\{\{([^}]+)\}\}/g,
        '<span class="replace-text-bracket">{{</span><span class="replace-text-static">$1</span><span class="replace-text-bracket">}}</span>'
      );

      setEnhancedContent(cleanedContent);
      setShowCompareModal(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.error ||
          "Failed to enhance text, please check your internet connection.";
        toast.error(errorMessage);
      } else {
        toast.error(
          "An unexpected error occurred while enhancing text, please check your internet connection."
        );
      }
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleAcceptChanges = () => {
    // Check if we're replacing content within a single paragraph
    // by checking if the selection spans multiple block nodes
    const { from, to } = selectionRange;
    const selectedDoc = editor.state.doc.slice(from, to);

    let blockCount = 0;
    selectedDoc.content.forEach((node: any) => {
      if (node.isBlock) {
        blockCount++;
      }
    });

    // Check if content is already HTML-formatted (contains HTML tags)
    const isHtmlFormatted = /<[^>]+>/.test(enhancedContent);

    // If selection is within a single block (like mid-paragraph),
    // insert as plain text to avoid breaking the paragraph
    if (blockCount <= 1) {
      // Just insert the text directly without any HTML wrapping
      editor
        .chain()
        .focus()
        .deleteRange(selectionRange)
        .insertContent(enhancedContent.replace(/<[^>]*>/g, "")) // Strip any HTML tags
        .run();
    } else if (isHtmlFormatted) {
      // Content is already HTML-formatted (from preference-based enhancement)
      // Insert it directly without additional formatting
      editor
        .chain()
        .focus()
        .deleteRange(selectionRange)
        .insertContent(enhancedContent)
        .run();
    } else {
      // Multiple blocks selected - format as proper paragraphs
      const formattedContent = enhancedContent
        .split(/\n\n+/) // Split on double line breaks
        .map((para) => para.trim())
        .filter((para) => para.length > 0)
        .map((para) => `<p>${para}</p>`)
        .join("");

      editor
        .chain()
        .focus()
        .deleteRange(selectionRange)
        .insertContent(formattedContent)
        .run();
    }

    // Record undo depth after enhance — if it drops below this, enhance was undone
    enhanceUndoDepthRef.current = undoDepth(editor.state);
    enhancedTextRef.current = enhancedContent;
    trackingStateRef.current = "tracking";

    // Save the case with the updated content
    if (onChange && caseId) {
      onChange({ content: { data: editor.getHTML() }, caseId });
    }

    // Log enhanced writing audit — save ID so we can delete on undo / re-create on redo
    if (caseId) {
      axios
        .post("/api/audits", {
          caseId,
          content: enhancedContent,
          audit_type: "enhanced_writing",
        })
        .then((res) => {
          auditIdRef.current = res.data.audit.id;
        })
        .catch((err) =>
          console.error("Failed to log enhanced writing audit:", err)
        );
      onAiGenerated?.();
    }

    setShowCompareModal(false);
  };

  const handleDiscardChanges = () => {
    setShowCompareModal(false);
  };

  // Mark the current selection as a custom redaction. Persists via the same
  // PATCH endpoint the Redactions sidebar uses, then pushes the updated list
  // into the redactions extension. If the sidebar's "Highlight in editor"
  // toggle is on, the new item gets painted immediately; if off, it's just
  // added to the list (consistent with the opt-in highlight design).
  const handleRedactSelection = async () => {
    if (!editor || !caseId) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc
      .textBetween(from, to, " ", " ")
      .trim();

    if (!selectedText) {
      toast.error("Please select some text to redact");
      return;
    }

    const existing = caseRedactions || [];
    if (
      existing.some(
        (r) => r.text.toLowerCase() === selectedText.toLowerCase(),
      )
    ) {
      toast("Already marked for redaction");
      return;
    }

    const newItem: RedactionType = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      text: selectedText,
      category: "Other",
      custom: true,
    };
    const next = [...existing, newItem];
    await persistRedactions(next, "Added to redactions");
  };

  // Remove every redaction that overlaps the current selection (the same set
  // that flipped the button to "Unredact").
  const handleUnredactSelection = async () => {
    if (!editor || !caseId || redactionsInSelection.length === 0) return;
    const removeIds = new Set(redactionsInSelection.map((r) => r.id));
    const next = (caseRedactions || []).filter((r) => !removeIds.has(r.id));
    await persistRedactions(
      next,
      removeIds.size === 1 ? "Redaction removed" : "Redactions removed",
    );
  };

  // Shared persistence used by both redact and unredact: PATCH the full list,
  // sync local state + the extension, and toast.
  const persistRedactions = async (
    next: RedactionType[],
    successMessage: string,
  ) => {
    if (!caseId) return;
    try {
      const res = await axios.patch(
        `/api/cases/${caseId}/redactions`,
        { redactions: next },
        { withCredentials: true },
      );
      const saved = (res.data?.redactions as RedactionType[]) || next;

      // Enable the highlight BEFORE pushing the new list, for BOTH redact and
      // unredact. We flip the extension on directly (synchronous) and also lift
      // the parent state (so the sidebar toggle reflects it). Doing it first
      // guarantees the decoration rebuild below runs with highlighting on —
      // relying solely on the parent state→effect chain can lag a render behind
      // this async path and leave the rebuilt set briefly disabled.
      editor.commands.setRedactionsEnabled(true);
      onEnableRedactionHighlight?.();

      setCaseRedactions?.(saved);
      editor.commands.setRedactions(saved);

      // Surface the Redactions side-nav so the user sees the updated list.
      onOpenRedactionsTab?.();
      toast.success(successMessage);
    } catch (err) {
      console.error("Failed to update redactions:", err);
      toast.error("Failed to update redactions");
    }
  };

  return (
    <>
      <BubbleMenu
        className={cn(
          showCompareModal && "hidden",
          "flex bg-base-100 shadow-md border border-base-300 rounded-lg p-1 gap-0.5"
        )}
        tippyOptions={{ duration: 100 }}
        editor={editor}
        shouldShow={({ editor, state }) => {
          // Don't show if modal is open
          if (showCompareModal) return false;

          // Don't show while the editor is read-only (e.g. during the
          // SuggestionsReview walk-through). Trying to mount tippy against
          // freshly mutated DOM crashes the bubble-menu plugin.
          if (!editor.isEditable) return false;

          // Check if selection is within a replace-text range
          const pluginState = replaceTextPluginKey.getState(state);
          if (pluginState && pluginState.ranges && !pluginState.hidden) {
            const { from, to } = state.selection;

            // Check if selection overlaps with any replace-text range
            const isInReplaceText = pluginState.ranges.some(
              (range: { from: number; to: number; text: string }) =>
                (from >= range.from && from <= range.to) ||
                (to >= range.from && to <= range.to) ||
                (from <= range.from && to >= range.to)
            );

            if (isInReplaceText) return false;
          }

          // Show bubble menu for normal text selections
          return !state.selection.empty;
        }}
      >
        <button
          onClick={handleEnhance}
          data-tour="enhance-writing"
          disabled={isEnhancing}
          className="flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-normal"
        >
          {isEnhancing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Wand2Icon className="w-4 h-4 text-blue-500 dark:text-blue-300" />
          )}
          <span>{isEnhancing ? "Enhancing..." : "Enhance Writing"}</span>
        </button>
        {caseId &&
          (() => {
            const isUnredact = redactionsInSelection.length > 0;
            return (
              <>
                <div className="w-px bg-base-300 my-1" />
                <button
                  onClick={
                    isUnredact
                      ? handleUnredactSelection
                      : handleRedactSelection
                  }
                  className="flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-base-200 text-sm font-normal"
                >
                  {isUnredact ? (
                    <EyeIcon className="w-4 h-4 text-blue-500 dark:text-blue-300" />
                  ) : (
                    <EyeOffIcon className="w-4 h-4 text-blue-500 dark:text-blue-300" />
                  )}
                  <span>{isUnredact ? "Unredact" : "Redact"}</span>
                </button>
              </>
            );
          })()}
      </BubbleMenu>

      <EnhanceCompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        originalContent={originalContent}
        enhancedContent={enhancedContent}
        onAccept={handleAcceptChanges}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default FloatingMenu;
